import { BookOpenTextIcon, CalendarCheckIcon, CalendarXIcon, ClipboardCheckIcon, FileTextIcon, FolderSearchIcon, LayersIcon, MessageCircleIcon, MicOffIcon, TimerIcon, UsersIcon } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

// Mockup «ENGELSK PARAT™ · 6 bind» (Recursos Impultienda, tienda ENGELSK PARAT).
export const PRODUCT_IMAGE = 'https://aekpgsbqjzqntlbucbts.supabase.co/storage/v1/object/public/store-resources/205b2b19-228d-4b45-9ecf-c49cdd83f9e2/1792720837538_engelskparat-mockup-caja-6-bind-5c3332f7.webp';
export const BRAND = 'ENGELSK PARAT™';
export const DISCLAIMER = 'Ikke en officiel udgivelse fra Børne- og Undervisningsministeriet. Materialet er bygget på Fælles Mål for engelsk – læs altid de gældende mål på emu.dk.';

export const heroGuarantees = ['Direkte download', 'Engangsbetaling', 'Intet abonnement', '7 dages pengene-tilbage-garanti'];

export const stats = [{
  value: '6',
  label: 'Digitale bind'
}, {
  value: '240',
  label: 'Sider klar til brug'
}, {
  value: '5',
  label: 'Bonusser inkluderet'
}, {
  value: 'PDF',
  label: 'Klar til print'
}, {
  value: '1–9',
  label: 'Klasse'
}];

export const differenceStats = [{
  value: '1.–9. klasse',
  label: 'Fra de første ord i indskolingen til argumentation og prøveforberedelse i udskolingen'
}, {
  value: '6 digitale bind',
  label: 'Fra årsplan og færdige lektioner til speaking, læsning, grammatik, kultur og evaluering'
}, {
  value: 'PDF klar til print',
  label: 'Print kopiarkene eller vis dem på tavlen'
}];

export const beforeItems: Array<{
  title: string;
  text: string;
}> = [{
  title: 'Søndag aften går med forberedelse',
  text: 'Du sidder foran computeren søndag aften og planlægger ugens engelsktimer – og bliver alligevel ikke færdig.'
}, {
  title: 'Store forskelle i samme klasse',
  text: 'Nogle taler næsten flydende efter YouTube og spil, andre tør knap sige «hello». Timen skal fungere for alle.'
}, {
  title: 'Eleverne tør ikke tale',
  text: 'Du vil have mere mundtligt engelsk, men der bliver stille i klassen, når eleverne skal tale.'
}, {
  title: 'Engelsk uden linjefag',
  text: 'Mange klasselærere underviser i engelsk uden at have det som linjefag – og skal lave alt selv.'
}, {
  title: 'Kompetencemål og evaluering',
  text: 'Hvad skal eleverne kunne efter 2., 4., 7. og 9. klasse – og hvordan evaluerer du det undervejs?'
}, {
  title: 'Jagten på materialer på nettet',
  text: 'De ark, du finder, passer sjældent til klassetrinnet, emnet eller niveauerne i netop din klasse.'
}];

export const afterItems = ['En færdig årsplan for 1.–2., 3.–4., 5.–7. og 8.–9. klasse', '12 færdige engelsklektioner minut for minut', 'Kopiark i 3 niveauer – klar til print', 'Key phrases på engelsk og lærerens instruktion på dansk', '50 speaking-aktiviteter, der får eleverne til at tale', 'Læsetekster og skriverammer i tre niveauer', 'Grammatik forklaret på dansk, øvelser på engelsk', 'Kultur og tekster fra den engelsktalende verden', 'Rubrikker, prøver med facitliste og 100 færdige feedbackkommentarer'];

export const recognizeItems: Array<{
  icon: LucideIcon;
  title: string;
  text: string;
}> = [{
  icon: CalendarXIcon,
  title: 'Søndag aften går med forberedelse',
  text: 'Bruger du søndag aften på at planlægge ugens engelsktimer helt fra bunden?'
}, {
  icon: UsersIcon,
  title: 'Klassen har vidt forskellige niveauer',
  text: 'Har du brug for lektioner, hvor eleven, der kæmper, og eleven, der taler flydende, kan arbejde med det samme – hver på sit niveau?'
}, {
  icon: MicOffIcon,
  title: 'Der bliver stille, når eleverne skal tale',
  text: 'Ville du ønske, at flere elever turde tale engelsk i timen?'
}, {
  icon: ClipboardCheckIcon,
  title: 'Evaluering tager for meget tid',
  text: 'Ville du ønske, at rubrikker, prøver med facitliste og feedback allerede var skrevet?'
}, {
  icon: TimerIcon,
  title: 'Klokken er 8.05, og intet er klar',
  text: 'Står du ved kopimaskinen lige før timen – uden et kopiark, der passer til klassen?'
}, {
  icon: FolderSearchIcon,
  title: 'Du leder efter materialer på nettet',
  text: 'Bruger du tid på at lede efter ark og aktiviteter, der passer til klassetrinnet og emnet?'
}];

// Spine colours mirror the box: navy, sky blue, green, lime, orange, red.
export const volumes: Array<{
  number: string;
  level: string;
  title: string;
  tagline: string;
  intro: string;
  items: string[];
  outro: string;
  note?: string;
  spine: string;
  rule: string;
}> = [{
  number: 'Bind 01',
  level: 'Årsplan',
  title: 'Årsplan og 12 færdige engelsklektioner',
  tagline: 'Vid præcis, hvad hver engelsktime skal indeholde – hele skoleåret.',
  intro: 'Det første bind giver dig årsplanen for hvert klassetrin og tolv færdige engelsklektioner, du kan bruge direkte.',
  items: ['Årsplan for 1.–2., 3.–4., 5.–7. og 8.–9. klasse', 'Koblet til kompetencemålene', 'Ugeplan og lektionsstruktur', 'Classroom language-plakat', '12 lektioner minut for minut', 'Kopiark i 3 niveauer til hver lektion', 'Facitliste til kopiarkene', 'Tjekliste og exit tickets'],
  outro: 'Færdige lektioner, så du møder klassen med en klar plan.',
  spine: 'bg-blue-900 text-white',
  rule: 'border-white/30'
}, {
  number: 'Bind 02',
  level: 'Speaking',
  title: 'Speaking: mundtligt engelsk, som alle tør',
  tagline: 'Halvtreds aktiviteter, der får hele klassen til at tale.',
  intro: 'Det andet bind samler speaking-aktiviteter fra indskoling til udskoling, hver med tid, organisering, fraser på engelsk og tre niveauer.',
  items: ['50 speaking-aktiviteter', 'Pair work og info gap', 'Rollespil og samtalekort', '«Talk for 1 minute»', 'Debatter til udskolingen', 'Sentence starters', 'Tryg klassekultur', 'Mundtlig evaluering'],
  outro: 'Mundtligt engelsk hver uge – uden at du selv skal finde på det hele.',
  spine: 'bg-sky-500 text-slate-900',
  rule: 'border-slate-900/20'
}, {
  number: 'Bind 03',
  level: '3 niveauer',
  title: 'Læsning og skrivning i tre niveauer',
  tagline: 'Samme tekst i tre niveauer – hele klassen taler om det samme bagefter.',
  intro: 'Det tredje bind giver dig læsetekster på engelsk i niveau A, B og C med spørgsmål, facitliste og skriverammer.',
  items: ['Læsetekster i tre niveauer', 'Spørgsmål med facitliste', 'Læsestrategier', 'Skriverammer: postcard, e-mail, story', 'Argumenterende tekst', 'Modeltekster'],
  outro: 'Undervisningsdifferentiering uden at lave tre versioner selv.',
  spine: 'bg-green-600 text-white',
  rule: 'border-white/30'
}, {
  number: 'Bind 04',
  level: 'Ordforråd',
  title: 'Grammatik og ordforråd uden kedsomhed',
  tagline: 'Forklaret på dansk. Øvet på engelsk. Med facitliste.',
  intro: 'Det fjerde bind giver dig ordlister efter emne og klassetrin og grammatik forklaret på dansk med øvelser og spil på engelsk.',
  items: ['Ordlister efter emne og klassetrin', 'Present simple og continuous', 'Past tense og irregular verbs', 'Question words', 'Prepositions og adjectives', 'Spil til repetition', 'Facitliste til alle øvelser'],
  outro: 'Grammatik, som eleverne faktisk husker.',
  spine: 'bg-lime-400 text-slate-900',
  rule: 'border-slate-900/20'
}, {
  number: 'Bind 05',
  level: 'Kultur',
  title: 'Den engelsktalende verden',
  tagline: 'Lande, kultur, oprindelige folk og højtider – med tekster og opgaver.',
  intro: 'Det femte bind tager klassen med ud i den engelsktalende verden med faktasider og tekster i tre niveauer.',
  items: ['Storbritannien, USA og Irland', 'Australien, Canada, Indien og Sydafrika', 'Oprindelige folk', 'Engelsk som verdenssprog', 'Halloween, Thanksgiving, Bonfire Night og Christmas', 'Interkulturel kompetence i praksis'],
  outro: 'Mødet med engelsksprogede tekster – klar til timen.',
  spine: 'bg-orange-500 text-slate-900',
  rule: 'border-slate-900/20'
}, {
  number: 'Bind 06',
  level: 'Evaluering',
  title: 'Evaluering og prøveforberedelse',
  tagline: 'Vid, hvad eleverne skal kunne – og hvordan du evaluerer det.',
  intro: 'Det sjette bind giver dig målene i et elevvenligt sprog, rubrikker, prøver og færdig feedback – og hjælp til forberedelsen af prøverne i 9. klasse.',
  items: ['Mål efter 2., 4., 7. og 9. klasse i elevvenligt sprog', '«I can»-sætninger', 'Rubrikker til mundtligt og skriftligt', 'Prøver A/B med facitliste', 'Forberedelse til mundtlig og skriftlig prøve i 9. klasse', 'Selvevaluering', '100 færdige feedbackkommentarer'],
  outro: 'Fremadrettet feedback uden aftenarbejde.',
  note: `${BRAND} er et selvstændigt udviklet materiale. ${DISCLAIMER}`,
  spine: 'bg-red-600 text-white',
  rule: 'border-white/30'
}];

export const volumesIntro = {
  title: '6 digitale bind til engelsklærere',
  text: 'Et komplet system, der hjælper dig med at planlægge og gennemføre engelskundervisningen gennem hele skoleåret. Hvert bind har 40 sider med færdige lektioner, kopiark og opgaver klar til print.'
};

export const packItems: Array<{
  icon: LucideIcon;
  title: string;
  text: string;
}> = [{
  icon: BookOpenTextIcon,
  title: 'Færdige lektioner minut for minut',
  text: 'Start · hoveddel · afslutning. Med materialer, lærerens instruktion på dansk og key phrases på engelsk.'
}, {
  icon: FileTextIcon,
  title: 'Kopiark klar til print',
  text: 'Kopiark til hver lektion, som du printer eller viser på tavlen – ingen leden i mapper.'
}, {
  icon: LayersIcon,
  title: 'Tre niveauer i hver lektion',
  text: 'Samme emne, tre niveauer. Elever, der kæmper, og elever, der taler flydende, arbejder sammen.'
}, {
  icon: MessageCircleIcon,
  title: 'Speaking, som alle tør',
  text: 'Pair work, rollespil, samtalekort og sentence starters, der gør det trygt at tale engelsk.'
}, {
  icon: ClipboardCheckIcon,
  title: 'Prøver, rubrikker og facitliste',
  text: 'Prøver A/B med facitliste, rubrikker til mundtligt og skriftligt og 100 færdige feedbackkommentarer.'
}, {
  icon: CalendarCheckIcon,
  title: 'Årsplan for hvert klassetrin',
  text: 'Emner måned for måned for 1.–2., 3.–4., 5.–7. og 8.–9. klasse – koblet til kompetencemålene i Fælles Mål.'
}];

export { pageSamples } from './pageSamples';
export const pageTypes = ['Årsplan', 'Færdige lektioner', 'Kopiark i 3 niveauer', 'Key phrases og facitliste'];

export const bonuses: Array<{
  title: string;
  text: string;
  items: string[];
  benefit: string;
}> = [{
  title: 'Vikarpakken',
  text: 'Færdige engelsklektioner, som vikaren kan gennemføre, når du er væk.',
  items: ['12 færdige lektioner', 'Ingen forberedelse', 'Tydelige instruktioner', 'Klar til print'],
  benefit: 'Syg eller på kursus? Engelsktimen kører alligevel.'
}, {
  title: 'Engelsk på 5 minutter',
  text: 'Printbare warm-up-kort, der får klassen i gang med engelsk på et øjeblik.',
  items: ['60 warm-up-kort', 'Alle klassetrin', 'Mundtlige opstarter', 'Klar til print'],
  benefit: 'En ny opstart hver time – uden forberedelse.'
}, {
  title: 'Klasseplakater',
  text: 'Farverige plakater til væggen i klasselokalet.',
  items: ['20 plakater', 'Classroom language', 'Question words og tenses', 'Days, months, alphabet og numbers'],
  benefit: 'Engelsk på væggen – hjælp til eleverne hver dag.'
}, {
  title: 'Engelskbingo og klassespil',
  text: 'Færdige spil, der gør repetition sjovt.',
  items: ['Bingoplader efter emne', 'Who am I?', 'Taboo-kort', 'Simon says'],
  benefit: 'Ti minutters spil – og ordene sidder fast.'
}, {
  title: 'Forældrekommunikation',
  text: 'Færdige beskeder til forældrene på dansk.',
  items: ['40 færdige beskeder', 'Skolestart og nyt emne', 'Lektier og prøver', 'Tips til engelsk derhjemme'],
  benefit: 'Beskeden til forældrene skrevet på et minut.'
}];

export const steps: Array<{
  title: string;
  text: string;
}> = [{
  title: `Køb ${BRAND}`,
  text: 'Gennemfør købet på et par minutter. Engangsbetaling, intet abonnement.'
}, {
  title: 'Download PDF-filerne fra din e-mail',
  text: 'Du får PDF-filerne tilsendt på e-mail lige efter købet.'
}, {
  title: 'Vælg lektionen og mød klassen',
  text: 'Find ugen i årsplanen, print kopiarket i tre niveauer – og mød klassen med en klar plan.'
}];

export const audienceItems = ['Underviser i engelsk i 1.–9. klasse', 'Er klasselærer og har engelsk uden linjefag', 'Har elever på vidt forskellige niveauer i samme klasse', 'Vil have flere elever til at tale engelsk', 'Vil slippe for at planlægge hver lektion fra bunden', 'Vil have kopiark, prøver og facitliste klar', 'Vil have evaluering koblet til kompetencemålene', 'Vil have søndag aften tilbage'];

export const timeValueTasks = ['Lede efter materialer på nettet', 'Planlægge ugens engelsktimer', 'Lave kopiark i flere niveauer', 'Lave prøver og facitliste', 'Skrive feedback'];

export const offerRows: Array<{
  label: string;
  detail: string;
}> = [{
  label: '6 digitale bind – 240 sider',
  detail: 'Årsplan og færdige lektioner, speaking, læsning og skrivning, grammatik og ordforråd, den engelsktalende verden og evaluering'
}, {
  label: '5 bonusser uden ekstra omkostninger',
  detail: 'Vikarpakken, warm-up-kort, klasseplakater, engelskbingo og klassespil og beskeder til forældre'
}, {
  label: 'Til engelsklærere i 1.–9. klasse',
  detail: 'Færdige lektioner, kopiark i 3 niveauer, key phrases, rubrikker og prøver med facitliste'
}];

export const offerIncludes = ['Direkte download', 'PDF-filer på e-mail', 'Klar til print', 'Ingen fysisk vare sendes'];
export const PRICE = '199 kr';
export const PRICE_BEFORE = '399 kr';

// No testimonials on this page. Only authentic reviews may ever be added here.
export const testimonials: Array<{
  quote: string;
  name: string;
  location: string;
}> = [];

export const faqs = [{
  q: 'Hvilket format har materialet?',
  a: `${BRAND} leveres digitalt som PDF-filer. Du kan bruge dem på computer, tablet eller mobil og printe de sider, du har brug for.`
}, {
  q: 'Hvordan får jeg materialet?',
  a: 'Du får PDF-filerne på e-mail lige efter købet. Ingen ventetid og ingen fysisk vare.'
}, {
  q: 'Hvilke klassetrin passer det til?',
  a: 'Til 1.–9. klasse. Årsplanerne og lektionerne er delt op i 1.–2., 3.–4., 5.–7. og 8.–9. klasse, og kopiarkene findes i tre niveauer.'
}, {
  q: 'Hvilket sprog er materialet på?',
  a: 'Lærerens instruktioner står på dansk. Alt elevmateriale – kopiark, key phrases, tekster og opgaver – står på engelsk.'
}, {
  q: 'Passer det til klasser med store forskelle?',
  a: 'Ja. Kopiarkene og læseteksterne findes i tre niveauer om samme emne, så hele klassen kan arbejde med det samme og tale sammen bagefter.'
}, {
  q: 'Kan jeg printe det?',
  a: 'Ja. Alt er lavet klar til print. Du vælger selv, hvad du printer, og du kan også vise siderne på tavlen.'
}, {
  q: 'Skal jeg have engelsk som linjefag for at bruge det?',
  a: 'Nej. Lektionerne er beskrevet minut for minut med lærerens instruktion på dansk og færdige key phrases på engelsk. Materialet passer både til faglærere og til klasselærere, der har engelsk.'
}, {
  q: 'Er det godkendt af Børne- og Undervisningsministeriet?',
  a: `Nej. ${BRAND} er et selvstændigt udviklet materiale. ${DISCLAIMER}`
}, {
  q: 'Hjælper det med prøverne i 9. klasse?',
  a: 'Bind 6 indeholder rubrikker, prøver med facitliste og materiale til forberedelse af den mundtlige og skriftlige prøve i engelsk. Materialet giver struktur og øvelse – det kan ikke love bestemte karakterer.'
}, {
  q: 'Kan jeg virkelig være klar til timen på 5 minutter?',
  a: `${BRAND} er lavet for at gøre forberedelsen hurtigere. Du finder ugen i årsplanen, læser lektionen igennem og printer kopiarket – og er klar på cirka fem minutter. Tiden kan variere efter lektionen, klassen og behovet for tilpasning.`
}, {
  q: 'Er de fem bonusser inkluderet?',
  a: `Ja. Alle fem bonusser er inkluderet uden ekstra omkostninger – det hele for ${PRICE}.`
}, {
  q: 'Er det et abonnement?',
  a: 'Nej. Du betaler én gang og får adgang til det digitale materiale. Ingen faste træk.'
}, {
  q: 'Er der en garanti?',
  a: `Ja. ${BRAND} har 7 dages pengene-tilbage-garanti. Hvis materialet ikke lever op til dine forventninger, kan du kontakte os og bede om at få pengene tilbage inden for 7 dage efter købet.`
}];

export const footerLinks = ['Købsbetingelser', 'Privatlivspolitik', 'Refusion', 'Kontakt'];
